<script setup lang="ts">
/**
 * Доска проекта — макет 9:2: колонки 280 с шагом 16, горизонтальный скролл и
 * перемотка к дальнему краю.
 *
 * На телефоне (макет 27:2) колонок в ряд не бывает: статусы становятся полосой
 * пилюль со счётчиками, а под ней стоит одна выбранная колонка во всю ширину.
 */
import { AppButton, useToast } from '@vinprop/design-system'
import { useEventListener, useIntervalFn, useResizeObserver } from '@vueuse/core'
import { AlertTriangle, ChevronsLeft, ChevronsRight, Columns3, SearchX } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage, errorRoute } from '@/api/errors'
import IssueFilterBar from '@/components/IssueFilterBar.vue'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import ViewSwitch from '@/components/ViewSwitch.vue'
import { useIsMobile } from '@/composables/useIsMobile'
import BoardColumn from '@/features/board/components/BoardColumn.vue'
import { emptyFilters } from '@/features/board/api'
import { useBoardStore } from '@/features/board/store'
import IssuePeek from '@/features/issue/components/IssuePeek.vue'
import CreateIssuePanel from '@/features/issues/components/CreateIssuePanel.vue'
import type { CreatedIssue } from '@/features/issues/api'

/** Как часто доска догоняет чужие переносы */
const REFRESH_INTERVAL = 30_000

const { t } = useI18n()
const toast = useToast()
const route = useRoute()
const router = useRouter()
const board = useBoardStore()

const projectKey = computed(() => String(route.params.key ?? ''))

// Первая ступень — раздел проектов, вторая — сам проект. До этого путь
// начинался с проекта, а вёл его клик в список: название проекта уводило из
// проекта.
const breadcrumbs = computed(() => [
  { label: t('nav.projects'), to: '/projects' },
  { label: board.project?.name || projectKey.value, to: `/p/${projectKey.value}/board` },
  { label: t('views.board') },
])

const isMobile = useIsMobile()

/** Колонка, открытая на телефоне: по умолчанию первая по порядку доски. */
const activeColumnId = ref('')

const visibleColumns = computed(() => {
  if (!isMobile.value) return board.columns

  const active = board.columns.find((column) => column.id === activeColumnId.value)

  return active ? [active] : board.columns.slice(0, 1)
})

// Доска приехала (или сменился проект) — выбранная колонка могла исчезнуть.
watch(
  () => board.columns,
  (columns) => {
    if (!columns.some((column) => column.id === activeColumnId.value)) {
      activeColumnId.value = columns[0]?.id ?? ''
    }
  },
  { immediate: true },
)

const columnsRef = ref<HTMLElement | null>(null)
const isScrollable = ref(false)
const atEnd = ref(false)

const updateScroll = () => {
  const element = columnsRef.value

  if (!element) return

  const max = element.scrollWidth - element.clientWidth

  isScrollable.value = max > 1
  atEnd.value = element.scrollLeft >= max - 1
}

/** Доска шире экрана уже на пяти колонках: перемотка к дальнему краю. */
const jumpToEdge = () => {
  const element = columnsRef.value

  if (!element) return

  element.scrollTo({ left: atEnd.value ? 0 : element.scrollWidth, behavior: 'smooth' })
}

useEventListener(columnsRef, 'scroll', updateScroll, { passive: true })
useResizeObserver(columnsRef, updateScroll)

const openBoard = async () => {
  await board.open(projectKey.value)

  // «Нет такого проекта» и «есть, но не для вас» — разные экраны, и различает
  // их только ответ сервера.
  const failureRoute = errorRoute(board.error, projectKey.value)

  if (failureRoute) await router.replace(failureRoute)
}

const onMove = async (issueId: string, columnId: string, beforeIssueId: string | undefined) => {
  try {
    await board.move(issueId, columnId, beforeIssueId)
  } catch (failure) {
    toast.error(errorMessage(failure, t('board.moveFailed')))
  }
}

const isCreateOpen = ref(false)
/** Из какой колонки позвали создавать; пусто — из шапки экрана */
const createColumnId = ref('')

const openCreate = (columnId = '') => {
  createColumnId.value = columnId
  isCreateOpen.value = true
}

// Доска перечитывается целиком: новая задача встаёт в конец своей колонки, а
// вписывать её в список руками значит гадать про позицию, которую только что
// назначил сервер.
const onCreated = async (issue: CreatedIssue) => {
  toast.success(t('issues.create.created', { key: issue.key }))

  await board.refresh()
}

// Обновление в фоне: соседний человек переносит карточку, и без него два
// экрана расходятся до перезагрузки страницы.
useIntervalFn(() => void board.refresh(), REFRESH_INTERVAL)

onMounted(openBoard)

// Переход между досками из сайдбара меняет только параметр маршрута — экран
// при этом не пересоздаётся.
watch(projectKey, openBoard)

/** Отказ правки из панели показываем тем же тостом, что и остальные */
const onPeekFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('issue.saveFailed')))
}
</script>

<template>
  <TrackerToolbar :breadcrumbs="breadcrumbs">
    <template v-if="board.project">
      <!-- На телефоне переключателя вида и фильтров в шапке нет: в 390 пикселей
           они не встают, а без них экран остаётся рабочим (макет 27:2). -->
      <ViewSwitch v-if="!isMobile" :project-key="board.project.key" />

      <IssueFilterBar
        v-if="!isMobile"
        :filters="board.filters"
        :project-id="board.project.id"
        :type-profile="board.project.typeProfile"
        @change="board.applyFilters"
      />
      <!-- В панели кнопка короткая — «Создать» (макет 34:295): что именно
           создаётся, говорит экран, на котором она стоит. -->
      <AppButton variant="primary" size="small" @click="openCreate()">
        {{ t('toolbar.create') }}
      </AppButton>
    </template>
  </TrackerToolbar>

  <div class="board">
    <div v-if="board.isLoading && !board.columns.length" class="board__columns">
      <div v-for="index in 4" :key="index" class="board__skeleton" />
    </div>

    <TrackerEmptyState
      v-else-if="board.error"
      :icon="AlertTriangle"
      :title="t('board.error.title')"
      :description="t('board.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="openBoard">{{ t('board.error.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty && board.isFiltered"
      :icon="SearchX"
      :title="t('board.nothingFound.title')"
      :description="t('board.nothingFound.description')"
    >
      <AppButton variant="secondary" @click="board.applyFilters(emptyFilters())">
        {{ t('toolbar.filters.reset') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="board.isEmpty"
      :icon="Columns3"
      :title="t('board.empty.title')"
      :description="t('board.empty.description')"
    >
      <AppButton variant="primary" @click="openCreate()">
        {{ t('issues.create.action') }}
      </AppButton>
    </TrackerEmptyState>

    <!-- Пилюли статусов: на телефоне они заменяют ряд колонок. -->
    <div v-else-if="isMobile" class="board__mobile">
      <div class="board__pills">
        <button
          v-for="column in board.columns"
          :key="column.id"
          type="button"
          class="bpill"
          :class="{ 'bpill--active': column.id === activeColumnId }"
          :aria-pressed="column.id === activeColumnId"
          @click="activeColumnId = column.id"
        >
          {{ column.name }} · {{ column.issues.length }}
        </button>
      </div>

      <BoardColumn
        v-for="column in visibleColumns"
        :key="column.id"
        class="board__single"
        :column="column"
        :is-dragging="board.isDragging"
        :landed-id="board.landedId"
        @move="onMove"
        @create="openCreate"
        @drag-start="board.beginDrag"
        @drag-end="board.endDrag"
      />
    </div>

    <div v-else ref="columnsRef" class="board__columns">
      <BoardColumn
        v-for="column in board.columns"
        :key="column.id"
        :column="column"
        :is-dragging="board.isDragging"
        :landed-id="board.landedId"
        @move="onMove"
        @create="openCreate"
        @drag-start="board.beginDrag"
        @drag-end="board.endDrag"
      />
    </div>

    <button
      v-if="isScrollable"
      type="button"
      class="board__jump"
      :title="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      :aria-label="atEnd ? t('board.scrollToStart') : t('board.scrollToEnd')"
      @click="jumpToEdge"
    >
      <ChevronsLeft v-if="atEnd" :size="18" />
      <ChevronsRight v-else :size="18" />
    </button>
  </div>

  <CreateIssuePanel
    v-if="board.project"
    v-model="isCreateOpen"
    :project-id="board.project.id"
    :type-profile="board.project.typeProfile"
    :column-id="createColumnId"
    @created="onCreated"
  />

  <IssuePeek @failed="onPeekFailed" />
</template>

<style scoped>
.board {
  /* Ровно остаток экрана под тулбаром: доска не скроллит страницу, скролл
     уходит внутрь — по горизонтали к колонкам, по вертикали внутрь колонки. */
  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  overflow: hidden;
}

/* Телефон: полоса пилюль сверху, под ней одна колонка во всю ширину. */
.board__mobile {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  padding: 12px 12px 0;
}

.board__pills {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  overflow-x: auto;
  /* Полоса режется краем экрана — это и подсказывает, что её листают. */
  margin: 0 -12px;
  padding: 0 12px 2px;
  scrollbar-width: none;
}

.board__pills::-webkit-scrollbar {
  display: none;
}

.bpill {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
  cursor: pointer;
}

.bpill--active {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: var(--color-text-inverse, #fff);
}

.board__single {
  width: 100%;
  min-height: 0;
}

.board__columns {
  display: flex;
  flex: 1;
  gap: 16px;
  min-height: 0;
  /* 16 по кругу и 16 между колонками — как в макете: первая колонка стоит на
     x=16 доски, последняя не прижимается к правому краю. */
  padding: 16px;
  overflow-x: auto;
  overflow-y: hidden;
}

.board__columns::-webkit-scrollbar {
  height: 8px;
}

.board__columns::-webkit-scrollbar-thumb {
  border-radius: var(--radius-full);
  background: var(--color-border-strong);
}

.board__skeleton {
  flex-shrink: 0;
  width: 280px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

.board__jump {
  position: absolute;
  right: 16px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
}

.board__jump:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
</style>
